import { MouseEventHandler } from 'react'
import { ControllerRenderProps, FieldError, FieldValues } from 'react-hook-form'
import ClearIcon from '@mui/icons-material/Clear'
import RoomOutlinedIcon from '@mui/icons-material/RoomOutlined'
import { IconButton, InputAdornment, TextField } from '@mui/material'

import { DefaultElementHandlers } from 'common/interfaces'
import InputLabel from 'components/InputLabel'

interface MapTextFieldProps {
  field: ControllerRenderProps<FieldValues, string>
  inputValue: string
  error?: FieldError
  placeholder?: string
  label: string
  openEditMode: () => void
  onFocus: DefaultElementHandlers['onFocus']
  required?: boolean
}

export default function MapTextField({
  field,
  inputValue,
  error,
  placeholder,
  label,
  openEditMode,
  onFocus,
  required = false,
}: MapTextFieldProps): JSX.Element {
  const handleClear: MouseEventHandler = (event) => {
    event.stopPropagation()
    field.onChange({})
  }

  const errorMessageId = `${field.name}-helper-text`

  return (
    <TextField
      {...field}
      id={field.name}
      value={inputValue}
      fullWidth
      required={required}
      error={error !== undefined}
      helperText={error?.message}
      inputRef={field.ref}
      placeholder={placeholder}
      label={<InputLabel label={label} />}
      onClick={openEditMode}
      onFocus={onFocus}
      inputProps={{
        'aria-invalid': error !== undefined,
        'aria-describedby': error !== undefined ? errorMessageId : undefined,
      }}
      FormHelperTextProps={{
        id: errorMessageId,
        role: error !== undefined ? 'alert' : undefined,
      }}
      InputProps={{
        readOnly: true,
        startAdornment: (
          <InputAdornment position="start">
            <RoomOutlinedIcon color="primary" />
          </InputAdornment>
        ),
        endAdornment:
          inputValue !== '' ? (
            <InputAdornment position="end">
              <IconButton onClick={handleClear} aria-label={`Clear ${label}`}>
                <ClearIcon />
              </IconButton>
            </InputAdornment>
          ) : null,
      }}
    />
  )
}
